import { useEffect, useState } from "react";
import { toast } from "sonner";
import { shallow } from "zustand/shallow";
import { RegulifyService } from "../../api/RegulifyService";
import { useLoading } from "../../context/LoadingContext";
import doc from "../../documents/sample device product specifications.txt";
import { useRiskManagerStore } from "../../store/useRiskManagerStore";
import { fetchAuthSession } from "aws-amplify/auth";
import { buildWsUrl } from "../../utils/wsUrl";

const HazardIdentification = ({ goToNextTab }) => {
  const { setLoading } = useLoading();
  const [selectedFiles, setSelectedFiles] = useState([]);
  const form = useRiskManagerStore((state) => state.form, shallow);
  const setForm = useRiskManagerStore((state) => state.setForm);
  // const setFormDefault = useRiskManagerStore((state) => state.setFormDefault);

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;

    setForm({
      ...form,
      [name]: type === "checkbox" ? (checked ? 1 : 0) : value,
    });
  };

  const validateForm = () => {
    const requiredFields = [
      "device_name",
      "device_code",
      "device_usage",
      "hazard_override_flag",
    ];

    for (const field of requiredFields) {
      if (form[field] == null || form[field] === "") {
        toast.warning(`Please fill in the ${field.replace(/_/g, " ")} field.`);
        return false;
      }
    }

    return true;
  };

  const WS_PING_INTERVAL_MS = 20000;

  const WS_ERROR_MESSAGE = "There was a problem processing your Hazard Identification request. Please try again later.";

  const UNHANDELLED_ERROR_MESSAGE = "Unhandelled Error fulfilling the request: "

  const WS_URL_CREATE_RISK_FILE = (token) => buildWsUrl("create_risk_file", token);

  const getAuthToken = async () => {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString();
  };

  const startKeepAlive = (ws) =>
    setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: "ping" }));
      }
    }, WS_PING_INTERVAL_MS);

  function handleParsedMessage(parsed, { setLoading }) {
    switch (parsed.type) {
      case "error":
        console.error("Processing error:", parsed.message);
        toast.error(`Error: ${parsed.message}`);
        setLoading(false);
        return true;

      case "info":
        console.info("Processing update:", parsed.message);
        toast.info(`Update: ${parsed.message}`);
        return true;

      case "pong":
        console.info("Received ping/pong reply:", parsed.message);
        return true;

      default:
        return false;
    }
  }

  const generateRiskFile = async () => {
    if (!validateForm()) return;

    if (!selectedFiles?.length) {
      toast.warning("Please select at least one file.");
      return;
    }

    const formData = new FormData();
    for (let i = 0; i < selectedFiles.length && i < 5; i++) {
      formData.append("prodspec_doc_path", selectedFiles[i]);
    }

    formData.append("llm_to_be_used", "gpt");
    formData.append("device_name", form["device_name"]);
    formData.append("device_code", form["device_code"]);
    formData.append("device_usage", form["device_usage"]);
    formData.append("device_type", form["device_type"]);
    formData.append("hazard_override_flag", form["hazard_override_flag"]);

    let docPath;

    try {
      setLoading(true, "Processing your file… This may take several minutes. Thank you for waiting.", "text-green-600");

      const response =
        await RegulifyService.riskmgr_upload_files(formData);

      if (response.status !== 200) {
        throw new Error("Failed to upload document. Try again.");
      }

      docPath = response.data.prodspec_doc_paths;
    } catch (error) {
      setLoading(false);
      console.error("Error during file processing:", error);
      toast.error("Error during file processing");
      return;
    }

    if (!docPath) return;

    let keepAliveInterval;

    try {
      const token = await getAuthToken();
      const ws = new WebSocket(WS_URL_CREATE_RISK_FILE(token));

      ws.binaryType = "arraybuffer";

      ws.onopen = () => {
        keepAliveInterval = startKeepAlive(ws);

        const payload = {
          device_name: form["device_name"],
          device_type: form["device_type"],
          llm_to_be_used: "gpt",
          prodspec_file: docPath,
          hazard_override_flag: form["hazard_override_flag"],
        };

        ws.send(JSON.stringify(payload));
        toast.info("Processing started...");
      };

      ws.onmessage = (event) => {
        const { data } = event;

        try {
          const parsed = JSON.parse(data);

          if (handleParsedMessage(parsed, { setLoading })) { return; }

          if (parsed.content && parsed.filename) {
            const byteCharacters = atob(parsed.content);
            const byteArray = new Uint8Array(
              [...byteCharacters].map((c) => c.charCodeAt(0)),
            );

            const blob = new Blob([byteArray], {
              type:
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });

            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = parsed.filename;
            link.click();

            goToNextTab();
          } else {
            console.log("Unexpected output content received:", parsed);
          }
        } catch {
          console.log("Non-JSON message received:", data);
        }
      };

      ws.onerror = () => {
        console.error(WS_ERROR_MESSAGE);
        toast.error(WS_ERROR_MESSAGE);
        setLoading(false);
      };

      ws.onclose = () => {
        console.log("WebSocket closed");
        clearInterval(keepAliveInterval);
        setLoading(false);
      };
    } catch (error) {
      console.error(UNHANDELLED_ERROR_MESSAGE, error);
      setLoading(false);
    }
  };

  return (
    <div className="pl-4">
      <div className="form-control mt-4 w-full max-w-md">
        <label className="label">
          <span className="label-text font-semibold">Device name:</span>
        </label>
        <input
          name="device_name"
          className="input input-bordered w-full"
          value={form.device_name || ""}
          onChange={handleChange}
          maxLength={50}
          onKeyDown={(e) => {
            // Allow control keys
            if (
              e.key === "Backspace" ||
              e.key === "Delete" ||
              e.key === "ArrowLeft" ||
              e.key === "ArrowRight" ||
              e.key === "Tab"
            ) return;

            // Block anything not in whitelist
            if (!/[a-zA-Z0-9 _-]/.test(e.key)) {
              e.preventDefault();
            }
          }}
          required
        />
        <span className="label text-xs text-slate-500">
          No Invalid chacaters allowed, up to max 50 characters.
        </span>
      </div>

      <div className="form-control mt-4 w-full max-w-md">
        <label className="label">
          <span className="label-text font-semibold">
            Override if device name exists:
          </span>
        </label>
        <input
          name="hazard_override_flag"
          type="checkbox"
          className="checkbox ml-4"
          checked={form.hazard_override_flag === 1}
          onChange={handleChange}
          required
        />
        <span className="label text-xs text-slate-500">
          Check this to re-run Hazard Identification for the same device (device name determines identity).
        </span>
      </div>

      <div className="form-control mt-4 w-full max-w-md">
        <label className="label">
          <span className="label-text font-semibold">Device code:</span>
        </label>
        <input
          name="device_code"
          className="input input-bordered w-full"
          placeholder="Comma separated device codes if device carries multiple e.g. ITI, ITQ"
          value={form.device_code || ""}
          onChange={handleChange}
          required
        />
        <span className="label text-xs text-slate-500">
          Comma separated device codes if device carries multiple e.g. ITI, ITQ
        </span>
      </div>

      <div className="form-control mt-4 w-full max-w-md">
        <label className="label">
          <span className="label-text font-semibold">High Usage Device:</span>
        </label>
        <input
          name="device_usage"
          type="checkbox"
          className="checkbox ml-4"
          checked={form.device_usage === 1}
          onChange={handleChange}
          required
        />
      </div>

      <div className="form-control mt-4 w-full max-w-md">
        <label className="label">
          <span className="label-text font-semibold">Device Type:</span>
        </label>
        <div className="form-control mt-4 w-full max-w-md">
          <div
            key={form.device_type} //FIX: force re-render of entire group
            className="flex items-center gap-6 ml-2"
          >
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="device_type"
                value="0"
                className="radio"
                checked={form.device_type === "0"}
                onChange={handleChange}
                required
              />
              <span className="label-text">Software Only</span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="device_type"
                value="1"
                className="radio"
                checked={form.device_type === "1"}
                onChange={handleChange}
              />
              <span className="label-text">Mechanical Only</span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="device_type"
                value="2"
                className="radio"
                checked={form.device_type === "2"}
                onChange={handleChange}
              />
              <span className="label-text">Combination</span>
            </label>
          </div>
        </div>
        <div className="form-control mt-4 w-full max-w-md">
          <label className="label">
            <span className="label-text font-semibold">
              Select Multiple Product Specification Documents (Up to 5 docs)
            </span>
          </label>
          <input
            type="file"
            accept=".pdf,.docx,.txt"
            id="prodspecFile"
            className="file-input file-input-bordered w-full"
            multiple
            onChange={(e) => {
              const files = Array.from(e.target.files || []);

              // Separate valid and invalid files
              const validFiles = files.filter(file => file.size > 0);
              const emptyFiles = files.filter(file => file.size === 0);

              // Notify user about empty files
              if (emptyFiles.length > 0) {
                alert(
                  `These files are empty and were ignored:\n${emptyFiles
                    .map(f => f.name)
                    .join(", ")}`
                );
              }

              // If no valid files, stop here
              if (validFiles.length === 0) {
                setSelectedFiles([]);
                return;
              }

              // Optional: enforce max 5 files (since you mentioned limit)
              const limitedFiles = validFiles.slice(0, 5);

              if (validFiles.length > 5) {
                alert("You can upload a maximum of 5 files.");
              }

              setSelectedFiles(limitedFiles);
            }}
          />

          <p className="mt-1 mb-1 text-sm text-gray-500">
            Accepted formats:{" "}
            <span className="font-medium">.pdf, .docx, .txt</span>
          </p>
          <a
            class="link mt-1 text-sm text-gray-400 hover:text-gray-500"
            download={"sample device product specifications.txt"}
            href={doc}
            target="_blank"
            rel="noreferrer"
          >
            Download Product Specification Document sample
          </a>
        </div>
      </div>
      <button
        className="btn btn-primary w-fitmt-4 tooltip tooltip-right mt-4 w-fit rounded px-4 py-2"
        data-tip="Click to start the hazard identification process after entering your device details"
        onClick={generateRiskFile}
      >
        Identify Hazards Per ISO-14971-2019 Medical Devices Guidelines
      </button>
      <p className="mt-2 text-sm text-gray-500">
        Processing may take up to 60 minutes, depending on product specification
        file contents. Check your downloads folder for the identified hazard
        output file.
      </p>
    </div>
  );
};
export default HazardIdentification;
